import React from 'react';
import { FormGroup, Input } from '@snsw-gel/react';
import { useStoreDispatch, useStoreSelector } from 'store';
import {
  tfNSWCustomerSliceActions,
  validationInvalidValue,
  validationRequiredValue,
} from 'store/slice/tfNSWCustomer';
import type {
  TFNSWCardData,
  ValidationFormInvalid,
} from 'store/slice/tfNSWCustomer';

const InputTemplate: React.FC<InputTemplateProps> = ({
  dataKey,
  label,
  requiredErrorMessage,
  helpMessage = '',
  isSpaceAllowed = true,
  isRequired = false,
}) => {
  const dispatch = useStoreDispatch();
  const inputValue = useStoreSelector(
    (state) => state.tfNSWCustomer.data[dataKey]
  );
  const inputValidation = useStoreSelector(
    (state) => state.tfNSWCustomer.validation[dataKey]
  );
  
  const updateCustomerData = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      tfNSWCustomerSliceActions.setCardData({
        key: dataKey,
        value: event.target.value,
      })
    );
  };

  const invalidSpacesMessage = (fieldName: string) => `Enter your ${fieldName} as it appears on the card. If you don’t have a ${fieldName}, enter a hyphen ‘-‘`;
  
  // Function to check space/hyphen related issues
    const getSpaceErrorMessage = (value: string, fieldName: string) => {
      const hasOnlySpaces = /^[\s]*$/.test(value);
      const hasMultipleSpaces = /\s{2,}/.test(value);
      const hasLeadingOrTrailingSpaces = /^[\s]/.test(value) || /[\s]$/.test(value);
      const hasMultipleConHyphen = /-{2,}/.test(value);
      const hasHyphenFollSpace = /^- /.test(value);

      if (hasOnlySpaces || hasMultipleSpaces || hasLeadingOrTrailingSpaces || hasMultipleConHyphen || hasHyphenFollSpace) {
        return invalidSpacesMessage(fieldName); 
      }
      return null;
    };

    // Regex to validate a valid name
    const regexValidNameCharacters =/^[a-zA-ZàáâäãåąčćçęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñšžÀÁÂÄÃÅĄĆČÇĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßŒÆŠŽ∂ð '-]+$/;
    const invalidCharacterMessage = (fieldName: string) => `Special characters, symbols and full stops are not accepted. Check your details and try again. If you don’t have a ${fieldName}, enter a hyphen ‘-‘`;
    
    const invalidFieldMessage = `Special characters, spaces or full stops are not accepted. Check your details and try again.`;
    const regexValidFieldCharacters = /^[a-zA-ZàáâäãåąčćçęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñšžÀÁÂÄÃÅĄĆČÇĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßŒÆŠŽ∂ð]+$/;

    // Function to check character
    const getNameErrorMessage = (value: string, fieldName: string) => {
      if (!regexValidNameCharacters.test(value)) {
        return invalidCharacterMessage(fieldName);
      }
      return null;
    };

    const getFieldErrorMessage = (value: string) => {
      if (!regexValidFieldCharacters.test(value)) {
        return invalidFieldMessage;
      }
      return null;
    };

    const getErrorMessages = (error: ValidationFormInvalid, value: string, fieldName: string) => {
      if (error === validationInvalidValue) {
        if (['Given name', 'Middle name (optional)', 'Family name'].includes(fieldName)) {
          const spaceError = getSpaceErrorMessage(value, fieldName);
          if (isSpaceAllowed && spaceError) {
            return spaceError; // Return specific space/hyphenrelated message with field name
          }

          const nameError = getNameErrorMessage(value, fieldName);
          if (isSpaceAllowed && nameError) {
            return nameError; // Return invalid charater related error message
          }
        }    

        if (!['Given name', 'Middle name (optional)', 'Family name'].includes(fieldName)) {
          const fieldError = getFieldErrorMessage(value);
          if (isSpaceAllowed && fieldError) {
            return fieldError; // Return invalid charater related error message
          }
        }
        
      }

      if (error === validationRequiredValue) {
        return requiredErrorMessage;
      }

      return '';
    };

  return (
    <FormGroup
      id={`${subFormIdPrefix}${dataKey}`}
      label={label}
      helpMessage={helpMessage}
      hasError={Boolean(inputValidation)}
      errorMessage={getErrorMessages(inputValidation, inputValue ?? '', label)}
    >
      <Input
        required={isRequired}
        value={inputValue ?? ''}
        onChange={updateCustomerData}
      />
    </FormGroup>
  );
};

export default InputTemplate;

interface InputTemplateProps {
  dataKey: keyof TFNSWCardData;
  label: string;
  requiredErrorMessage: string;
  helpMessage?: string;
  isSpaceAllowed?: boolean;
  isRequired?: boolean;
}

export const subFormIdPrefix = 'tfnsw-';
